import ContentCopy from "@mui/icons-material/ContentCopy";
import DeleteOutline from "@mui/icons-material/DeleteOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { IconButton, Stack, type SxProps, Tooltip } from "@mui/material";
import type { ReactNode } from "react";

/**
 * RowActionIcons — three inline icons (edit / duplicate / delete) for data
 * grid rows. Replaces the legacy three-dot meatball menu so common actions
 * are one click instead of two. Supports custom action sets via `actions`.
 *
 * Default actions: Edit (pencil), Duplicate (copy), Delete (trash). Pass
 * `onEdit` / `onDuplicate` / `onDelete` for the standard set, or supply
 * `actions` for full control.
 */
const ICON_BTN_SX: SxProps = {
	width: 30,
	height: 30,
	borderRadius: 8,
	color: "rgba(13, 31, 48, 0.45)",
	transition: "background 0.18s, color 0.18s, box-shadow 0.18s",
	"&:hover": {
		background: "rgba(2, 213, 171, 0.10)",
		color: "#02D5AB",
		boxShadow: "0 0 0 3px rgba(2, 213, 171, 0.10), 0 2px 6px rgba(2, 213, 171, 0.18)"
	},
	"& svg": {
		fontSize: 16
	}
};

const DESTRUCTIVE_BTN_SX: SxProps = {
	...ICON_BTN_SX,
	"&:hover": {
		background: "rgba(224, 48, 48, 0.10)",
		color: "#E03030",
		boxShadow: "0 0 0 3px rgba(224, 48, 48, 0.10), 0 2px 6px rgba(224, 48, 48, 0.18)"
	}
};

export interface RowAction {
	key: string;
	label: string;
	icon: ReactNode;
	onClick: (event?: React.MouseEvent) => void;
	destructive?: boolean;
	disabled?: boolean;
	disabledLabel?: string;
}

export interface RowActionIconsProps {
	onEdit?: (event?: React.MouseEvent) => void;
	onDuplicate?: (event?: React.MouseEvent) => void;
	onDelete?: (event?: React.MouseEvent) => void;
	deleteDisabled?: boolean;
	deleteDisabledLabel?: string;
	/** Override the default edit/duplicate/delete trio with a custom action list. */
	actions?: RowAction[];
}

export default function RowActionIcons({
	onEdit,
	onDuplicate,
	onDelete,
	deleteDisabled,
	deleteDisabledLabel,
	actions
}: RowActionIconsProps) {
	const defaultItems: (RowAction | false)[] = [
		onEdit ? { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: onEdit } : false,
		onDuplicate ? { key: "duplicate", label: "Duplicate", icon: <ContentCopy />, onClick: onDuplicate } : false,
		onDelete ? {
			key: "delete",
			label: "Delete",
			icon: <DeleteOutline />,
			onClick: onDelete,
			destructive: true,
			disabled: deleteDisabled,
			disabledLabel: deleteDisabledLabel
		} : false
	];
	const items: RowAction[] = actions ?? defaultItems.filter((it): it is RowAction => Boolean(it));

	return (
		<Stack direction="row" spacing={0.25} sx={{ pr: 0.5, alignItems: "center" }}>
			{items.map((item) => {
				const tooltipTitle = item.disabled && item.disabledLabel ? item.disabledLabel : item.label;
				const button = (
					<IconButton
						size="small"
						aria-label={tooltipTitle}
						disabled={item.disabled}
						onClick={(e) => {
							e.stopPropagation();
							item.onClick(e);
						}}
						sx={item.destructive ? DESTRUCTIVE_BTN_SX : ICON_BTN_SX}
					>
						{item.icon}
					</IconButton>
				);

				return (
					<Tooltip key={item.key} title={tooltipTitle} arrow placement="top">
						{item.disabled ? <span>{button}</span> : button}
					</Tooltip>
				);
			})}
		</Stack>
	);
}
